import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FaList, FaCalendarAlt, FaExternalLinkAlt, FaSpinner, FaCrown, FaExclamationTriangle, FaVideo, FaClock, FaUser, FaInfoCircle, FaTimes, FaArrowRight, FaHourglassHalf, FaHistory } from 'react-icons/fa';
import './my_meetings.css';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuthStore } from '../../../../auth/state';
import DashboardHeader from '../../../../components/DashboardHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { db, auth } from '../../../../auth/firebase';
import cacheManager from '../../../../auth/cache_manager';

// # Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="my_meetings_error">
          <FaExclamationTriangle />
          <span>Something went wrong. Please try refreshing the page.</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// # Main Component
function MyMeetings() {
  const { userProfile } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hostData, setHostData] = useState({});
  const [calendarKey, setCalendarKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Memoized tab configuration
  const tabs = useMemo(() => [
    {
      label: 'Meeting List',
      icon: <FaList className="my_meetings_tab_icon" />,
    },
    {
      label: 'Calendar View',
      icon: <FaCalendarAlt className="my_meetings_tab_icon" />,
    }
  ], []);

  // # Optimized user data fetching
  const fetchUserData = useCallback(async (meetings) => {
    if (!meetings || meetings.length === 0) return;
    
    // # Extract unique host UIDs
    const uniqueHostIds = [...new Set(
      meetings
        .filter(meeting => meeting && meeting.host && meeting.host.uid)
        .map(meeting => meeting.host.uid)
    )];
    
    if (uniqueHostIds.length === 0) {
      console.log("No valid host IDs found in meetings");
      return;
    }
    
    try {
      const userDataPromises = uniqueHostIds.map(async (uid) => {
        try {
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            return [uid, {
              displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'Unknown',
              email: data.email || 'No email',
              avatar: data.photoURL || null,
              username: data.username || null
            }];
          }
          return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
        } catch (userError) {
          console.error(`Error fetching data for user ${uid}:`, userError);
          return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
        }
      });

      const userDataResults = await Promise.all(userDataPromises);
      const hostDataObj = Object.fromEntries(userDataResults);
      setHostData(hostDataObj);
      return hostDataObj;
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user information. Some host details may be missing.');
      return {};
    }
  }, []);

  // # Optimized meetings fetch with debounce
  const fetchMeetings = useCallback(async () => {
    if (!userProfile?.uid) return;

    setLoading(true);
    setError('');
    
    try {
      const meetingTypes = ['gen_meetings', 'step1_meetings', 'step2_meetings', 'oet_meetings', 'visa_meetings'];
      const allMeetings = [];

      // # First try to get meetings from the user's notifications
      try {
        const userNotifyRef = doc(db, 'user_notify', userProfile.uid);
        const userNotifyDoc = await getDoc(userNotifyRef);
        
        if (userNotifyDoc.exists()) {
          const notifyData = userNotifyDoc.data();
          
          // # Process meeting notifications if they exist
          if (notifyData.meetings) {
            // # Handle both map (new format) and array (old format) for backward compatibility
            if (Array.isArray(notifyData.meetings) && notifyData.meetings.length > 0) {
              // # Old array format
              console.log(`Found ${notifyData.meetings.length} meetings in user notifications (array format)`);
              
              notifyData.meetings.forEach(meeting => {
                // # Ensure the meeting has an ID
                if (!meeting.id) return;
                
                allMeetings.push({
                  ...meeting,
                  // # Handle various timestamp formats for meet_time
                  meet_time: meeting.meet_time instanceof Date 
                    ? meeting.meet_time 
                    : typeof meeting.meet_time === 'string' 
                      ? new Date(meeting.meet_time) 
                      : meeting.meet_time && typeof meeting.meet_time.toDate === 'function'
                        ? meeting.meet_time.toDate()
                        : new Date(meeting.meet_time)
                });
              });
            } else if (typeof notifyData.meetings === 'object' && Object.keys(notifyData.meetings).length > 0) {
              // # New map format
              console.log(`Found ${Object.keys(notifyData.meetings).length} meetings in user notifications (map format)`);
              
              // # Convert the map to an array of meeting objects
              Object.values(notifyData.meetings).forEach(meeting => {
                // # Ensure the meeting has an ID
                if (!meeting.id) return;
                
                allMeetings.push({
                  ...meeting,
                  // # Handle various timestamp formats for meet_time
                  meet_time: meeting.meet_time instanceof Date 
                    ? meeting.meet_time 
                    : typeof meeting.meet_time === 'string' 
                      ? new Date(meeting.meet_time) 
                      : meeting.meet_time && typeof meeting.meet_time.toDate === 'function'
                        ? meeting.meet_time.toDate()
                        : new Date(meeting.meet_time)
                });
              });
            }
          }
        }
      } catch (notifyError) {
        console.error("Error fetching meetings from notifications:", notifyError);
      }

      // # If we're an admin or mentor, also try to fetch from meetings collection directly
      const isAdmin = userProfile.isAdmin || userProfile.admin || 
                      (userProfile.role && ['admin', 'mentor', 'tutor'].includes(userProfile.role));
      
      if (isAdmin) {
        await Promise.all(meetingTypes.map(async (type) => {
          try {
            const meetingsRef = doc(db, 'meetings', type);
            const docSnap = await getDoc(meetingsRef);
            
            if (docSnap.exists()) {
              const data = docSnap.data();
              
              // # Extract meetings from object where keys are meeting IDs
              const typeMeetings = Object.entries(data)
                .map(([id, meeting]) => ({
                  id,
                  type,
                  ...meeting,
                  // # Handle various timestamp formats
                  meet_time: meeting.meet_time instanceof Date 
                    ? meeting.meet_time 
                    : typeof meeting.meet_time === 'string' 
                      ? new Date(meeting.meet_time) 
                      : meeting.meet_time && typeof meeting.meet_time.toDate === 'function'
                        ? meeting.meet_time.toDate()
                        : new Date(meeting.meet_time)
                }))
                // # Include meetings where user is either the host or is invited
                .filter(meeting => 
                  (meeting.host && meeting.host.uid === userProfile.uid) || 
                  (meeting.invited && Array.isArray(meeting.invited) && meeting.invited.includes(userProfile.uid))
                );
                
              // # Add filtered meetings to our collection, avoiding duplicates
              typeMeetings.forEach(meeting => {
                // Check for duplicates by ID
                if (!allMeetings.some(m => m.id === meeting.id)) {
                  allMeetings.push(meeting);
                }
              });
            }
          } catch (error) {
            console.error(`Error fetching ${type}:`, error);
          }
        }));
      }

      // # Sort by meeting time
      allMeetings.sort((a, b) => {
        // # Ensure valid dates for comparison
        const dateA = a.meet_time instanceof Date ? a.meet_time : new Date();
        const dateB = b.meet_time instanceof Date ? b.meet_time : new Date();
        return dateB - dateA;
      });
      
      setMeetings(allMeetings);
      
      // # Fetch host data if we have meetings
      if (allMeetings.length > 0) {
        await fetchUserData(allMeetings);
      } else {
        console.log("No meetings found for this user");
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError('Failed to load meetings. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [userProfile?.uid, userProfile?.isAdmin, userProfile?.admin, userProfile?.role, fetchUserData]);

  // # Initial meetings fetch
  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // # Memoized filtered meetings
  const filteredMeetings = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    
    // # If no search query, return all meetings
    if (!searchLower) return meetings;
    
    return meetings.filter(meeting => {
      if (!meeting) return false;
      
      const host = meeting.host?.uid ? hostData[meeting.host.uid] || {} : {};
      
      // # Safe access to searchable fields
      const title = meeting.title || '';
      const description = meeting.description || '';
      const hostName = host.displayName || '';
      const hostEmail = host.email || '';
      
      return (
        title.toLowerCase().includes(searchLower) ||
        description.toLowerCase().includes(searchLower) ||
        hostName.toLowerCase().includes(searchLower) ||
        hostEmail.toLowerCase().includes(searchLower)
      );
    });
  }, [meetings, searchQuery, hostData]);

  // # Group meetings into sections
  const groupedMeetings = useMemo(() => {
    const now = new Date();
    const within24Hours = [];
    const within7Days = [];
    const otherMeetings = [];

    filteredMeetings.forEach(meeting => {
      // # Ensure meet_time is a valid Date object
      let meetingTime;
      try {
        meetingTime = meeting.meet_time instanceof Date 
          ? meeting.meet_time 
          : new Date(meeting.meet_time);
      } catch (e) {
        console.error('Invalid meeting time format:', meeting.meet_time);
        meetingTime = now; // # Default to current time if invalid
      }
      
      // # Skip invalid dates
      if (isNaN(meetingTime.getTime())) {
        console.warn('Invalid meeting time for meeting:', meeting.id);
        return;
      }
      
      const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
      const daysUntilMeeting = hoursUntilMeeting / 24;
      
      // # Skip past meetings in categorization
      if (hoursUntilMeeting < 0) {
        otherMeetings.push(meeting);
        return;
      }
      
      if (hoursUntilMeeting <= 24) {
        within24Hours.push(meeting);
      } else if (daysUntilMeeting <= 7) {
        within7Days.push(meeting);
      } else {
        otherMeetings.push(meeting);
      }
    });
    
    // # Sort each section by meeting time
    const sortByTime = (a, b) => new Date(a.meet_time) - new Date(b.meet_time);
    within24Hours.sort(sortByTime);
    within7Days.sort(sortByTime);
    otherMeetings.sort(sortByTime);
    
    return {
      within24Hours,
      within7Days,
      otherMeetings
    };
  }, [filteredMeetings]);

  // # Memoized calendar events
  const events = useMemo(() => meetings.map(meeting => {
    // # Ensure meet_time is a valid Date object
    let meetingTime;
    try {
      meetingTime = meeting.meet_time instanceof Date 
        ? meeting.meet_time 
        : new Date(meeting.meet_time);
    } catch (e) {
      console.error('Invalid meeting time format:', meeting.meet_time);
      meetingTime = new Date(); // # Default to current time if invalid
      return null; // Skip invalid meetings
    }
    
    // # Skip invalid dates
    if (isNaN(meetingTime.getTime())) {
      console.warn('Invalid meeting time for meeting:', meeting.id);
      return null;
    }
    
    // # Calculate time until meeting
    const now = new Date();
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
    const hoursAfterMeeting = (now - meetingTime) / (1000 * 60 * 60);

    // # Determine event class based on time
    let eventClass = '';
    let eventStatus = '';
    
    if (hoursUntilMeeting < -6) {
      eventClass = 'past-meeting';
      eventStatus = 'Past';
    } else if (hoursAfterMeeting > 0 && hoursAfterMeeting <= 6) {
      eventClass = 'joinable past-active';
      eventStatus = `Active (${Math.ceil(6 - hoursAfterMeeting)}h left)`;
    } else if (hoursUntilMeeting <= 1 && hoursUntilMeeting > -6) {
      eventClass = 'joinable';
      eventStatus = 'Join Now';
    } else if (hoursUntilMeeting <= 12 && hoursUntilMeeting > 1) {
      eventClass = 'upcoming-soon';
      eventStatus = 'Coming Soon';
    }

    return {
      id: meeting.id,
      title: meeting.title || 'Untitled Meeting',
      start: meetingTime,
      end: new Date(meetingTime.getTime() + 3600000), // # Add 1 hour
      hostData: hostData[meeting.host?.uid] || {
        displayName: 'Loading...',
        email: '',
        avatar: null,
        username: null
      },
      className: eventClass,
      display: 'block',
      hoursAfterStart: hoursAfterMeeting > 0 ? Math.floor(hoursAfterMeeting) : 0,
      status: eventStatus
    };
  }).filter(Boolean), [meetings, hostData]); // Filter out null events

  // # Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const id = event.id;
    const foundMeeting = meetings.find(m => m.id === id);
    
    if (foundMeeting) {
      setSelectedEvent({
        ...foundMeeting,
        startDate: event.start,
        className: event.classNames.join(' '),
        hoursAfterStart: event.extendedProps.hoursAfterStart,
        status: event.extendedProps.status,
        host: foundMeeting.host || {},
        hostData: hostData[foundMeeting.host?.uid] || {}
      });
    }
  };

  // # Close event details modal
  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  // # Format date for modal display
  const formatModalDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Africa/Cairo'
    });
  };
  
  // # Format time for modal display
  const formatModalTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Cairo'
    });
  };

  // # Format date and time helper
  const formatDateTime = useCallback((dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      })
    };
  }, []);

  // # Handle window resize
  useLayoutEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // # Adjust calendar view based on screen size
      if (window.innerWidth < 768) {
        setCalendarView('dayGridDay');
      } else {
        setCalendarView('dayGridMonth');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // # Optimized calendar options with responsive handling
  const calendarOptions = useMemo(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: calendarView,
    headerToolbar: windowWidth < 768 ? {
      left: 'prev,next',
      center: 'title',
      right: 'dayGridDay,dayGridWeek'
    } : {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    events,
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: windowWidth < 768 ? 2 : true,
    height: '100%',
    aspectRatio: windowWidth < 768 ? 0.8 : 1.35,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      meridiem: 'short'
    },
    eventContent: renderEventContent,
    slotMinTime: '06:00:00',
    slotMaxTime: '22:00:00',
    allDaySlot: false,
    nowIndicator: true,
    weekNumbers: windowWidth >= 768,
    weekNumberFormat: { week: 'numeric' },
    dayCellClassNames: 'my_meetings_calendar_cell',
    handleWindowResize: true,
    contentHeight: 'auto',
    expandRows: false,
    fixedWeekCount: false,
    stickyHeaderDates: false,
    stickyFooterScrollbar: false,
    dayMinWidth: null,
    dayMaxEventRows: windowWidth < 768 ? 2 : 6,
    views: {
      dayGrid: {
        dayMaxEventRows: windowWidth < 768 ? 2 : 6
      },
      timeGrid: {
        dayMaxEventRows: windowWidth < 768 ? 2 : 6
      }
    },
    eventClick: handleEventClick
  }), [events, windowWidth, calendarView, handleEventClick]);

  // # Optimized meeting item rendering with truncation
  const renderMeetingItem = useCallback((meeting) => {
    const { date, time } = formatDateTime(meeting.meet_time);
    const host = hostData[meeting.host?.uid] || {};
    const isHost = meeting.host?.uid === userProfile.uid;
    
    // # Calculate time until meeting
    const now = new Date();
    const meetingTime = new Date(meeting.meet_time);
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
    const hoursAfterMeeting = (now - meetingTime) / (1000 * 60 * 60);
    
    // # Determine meeting states
    const isPastMeeting = hoursUntilMeeting < -6; // # Changed to consider 6 hour window
    const isJoinable = (hoursUntilMeeting <= 1 && hoursUntilMeeting > -6) || (hoursAfterMeeting <= 6 && hoursAfterMeeting > 0);
    const isUpcoming = hoursUntilMeeting <= 12 && hoursUntilMeeting > 1;
    
    // # Determine item class based on time
    let itemClass = 'my_meetings_item';
    if (isPastMeeting) {
      itemClass += ' past-meeting';
    } else if (isJoinable) {
      itemClass += ' joinable';
    } else if (isUpcoming) {
      itemClass += ' upcoming-soon';
    }
    
    // # Determine join button state and text
    const joinButtonClass = `my_meetings_action_btn join ${isJoinable ? 'active' : ''} ${isUpcoming ? 'upcoming' : ''}`;
    const getJoinButtonText = () => {
      if (isJoinable && hoursAfterMeeting > 0) {
        return `Active (${Math.floor(6 - hoursAfterMeeting)}h left)`;
      }
      return isJoinable ? "Join Now" : isUpcoming ? "Coming Soon" : "Not Available";
    };
    
    return (
      <div key={meeting.id} className={itemClass}>
        <div className="my_meetings_item_header">
          <h3 title={meeting.title}>{meeting.title}</h3>
          {!isPastMeeting && meeting.host?.url && (
            <div className="my_meetings_item_actions">
              <a 
                href={meeting.host.url}
                target="_blank"
                rel="noopener noreferrer"
                className={joinButtonClass}
                title={getJoinButtonText()}
                onClick={(e) => !isJoinable && e.preventDefault()}
              >
                <FaExternalLinkAlt />
                <span className="sr-only">
                  {getJoinButtonText()}
                </span>
              </a>
            </div>
          )}
        </div>
        <p className="my_meetings_item_desc" title={meeting.description}>
          {meeting.description}
        </p>
        <div className="my_meetings_host_info">
          {host.avatar ? (
            <img 
              src={host.avatar} 
              alt={host.displayName} 
              className="my_meetings_host_avatar"
              loading="lazy"
            />
          ) : (
            <div className="my_meetings_host_avatar_placeholder">
              {host.displayName?.charAt(0) || '?'}
            </div>
          )}
          <div className="my_meetings_host_details">
            <div className="my_meetings_host_name_container">
              <span className="my_meetings_host_name text-ellipsis" title={host.displayName}>
                {host.displayName}
              </span>
              <span className="my_meetings_host_badge">
                <FaCrown /> Host
              </span>
            </div>
            <span className="my_meetings_host_email text-ellipsis" title={host.email}>
              {host.email}
            </span>
          </div>
        </div>
        <div className="my_meetings_item_details">
          <span className="my_meetings_item_date text-ellipsis" title={`${date} at ${time}`}>
            <FaCalendarAlt /> {date} at {time}
          </span>
          {isHost && (
            <span className="my_meetings_host_indicator text-ellipsis">
              You are the host
            </span>
          )}
        </div>
      </div>
    );
  }, [formatDateTime, hostData, userProfile?.uid]);

  // # Render a section of meetings
  const renderMeetingsSection = useCallback((title, meetings, iconClass) => {
    if (meetings.length === 0) return null;
    
    return (
      <div className="my_meetings_section">
        <div className="my_meetings_section_header">
          <h2>
            <span className={`my_meetings_section_icon ${iconClass}`}></span>
            {title}
            <span className="my_meetings_count">{meetings.length}</span>
          </h2>
        </div>
        <div className="my_meetings_items_container">
          {meetings.map(renderMeetingItem)}
        </div>
      </div>
    );
  }, [renderMeetingItem]);

  return (
    <ErrorBoundary>
      <div className={`my_meetings_container ${isDarkMode ? 'my_meetings_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        
        <div className="my_meetings_content">
          <div className="my_meetings_header">
            <div className="my_meetings_title_box">
              <h1>My Community Meetings</h1>
              <div className="my_meetings_title_underline"></div>
            </div>
          </div>

          {error && (
            <div className="my_meetings_error" role="alert">
              <FaExclamationTriangle />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="my_meetings_loading">
              <FaSpinner className="icon-spin" />
              <span>Loading your meetings...</span>
            </div>
          ) : (
            <div className="my_meetings_tabs">
              <Tabs
                selectedIndex={activeTab}
                onSelect={setActiveTab}
                selectedTabClassName="my_meetings_tab--selected"
              >
                <TabList className="my_meetings_tab_list">
                  {tabs.map((tab, index) => (
                    <Tab 
                      key={index}
                      className={`my_meetings_tab ${activeTab === index ? 'active' : ''}`}
                    >
                      {tab.icon}
                      <span className="my_meetings_tab_label">
                        {tab.label}
                        {activeTab === index && (
                          <span className="my_meetings_tab_active_indicator" />
                        )}
                      </span>
                    </Tab>
                  ))}
                </TabList>

                <TabPanel className={`my_meetings_tab_panel ${activeTab === 0 ? 'active' : ''}`}>
                  <div className="my_meetings_list_container">
                    <div className="my_meetings_search">
                      <input
                        type="text"
                        placeholder={windowWidth < 768 ? "Search meetings..." : "Search meetings by title, description, or host..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="my_meetings_search_input"
                      />
                    </div>
                    <div className="my_meetings_list">
                      {filteredMeetings.length > 0 ? (
                        <>
                          {renderMeetingsSection(
                            "Meetings in 24 Hours", 
                            groupedMeetings.within24Hours,
                            "upcoming"
                          )}
                          
                          {renderMeetingsSection(
                            "Meetings in 7 Days", 
                            groupedMeetings.within7Days,
                            "week"
                          )}
                          
                          {renderMeetingsSection(
                            "Other Meetings", 
                            groupedMeetings.otherMeetings,
                            "other"
                          )}
                        </>
                      ) : (
                        <div className="my_meetings_empty">
                          <FaCalendarAlt className="my_meetings_empty_icon" />
                          <p>No meetings found</p>
                          {searchQuery && <p>Try adjusting your search criteria</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </TabPanel>

                <TabPanel className={`my_meetings_tab_panel ${activeTab === 1 ? 'active' : ''}`}>
                  <div className="my_meetings_calendar_container">
                    <div className="my_meetings_calendar">
                      <FullCalendar
                        key={`${calendarKey}-${windowWidth}`}
                        {...calendarOptions}
                      />
                    </div>
                  </div>
                </TabPanel>
              </Tabs>
            </div>
          )}

          {/* # Event Details Modal */}
          {selectedEvent && (
            <div className="my_meetings_modal_overlay" onClick={handleCloseModal}>
              <div className="my_meetings_event_details" onClick={(e) => e.stopPropagation()}>
                <div className={`my_meetings_event_status_banner ${selectedEvent.className}`}>
                  {selectedEvent.status}
                </div>
                
                <div className="my_meetings_event_header">
                  <div className="my_meetings_event_type_container">
                    <FaVideo className="my_meetings_event_icon" />
                    <div className="my_meetings_event_type">
                      {selectedEvent.type?.replace('_meetings', '').toUpperCase() || 'MEETING'}
                    </div>
                  </div>
                  
                  <h1 className="my_meetings_event_title">{selectedEvent.title}</h1>
                </div>
                
                <div className="my_meetings_event_info_grid">
                  <div className="my_meetings_info_item">
                    <div className="my_meetings_info_label">
                      <FaUser className="my_meetings_info_icon" />
                      Host
                    </div>
                    <div className="my_meetings_info_value">
                      {selectedEvent.hostData.displayName || 'Unknown Host'}
                      {selectedEvent.host?.uid === userProfile.uid && 
                        <span className="my_meetings_host_badge_modal">(You)</span>
                      }
                    </div>
                  </div>
                  
                  <div className="my_meetings_info_item">
                    <div className="my_meetings_info_label">
                      <FaCalendarAlt className="my_meetings_info_icon" />
                      Date
                    </div>
                    <div className="my_meetings_info_value">
                      {formatModalDate(selectedEvent.meet_time)}
                    </div>
                  </div>
                  
                  <div className="my_meetings_info_item">
                    <div className="my_meetings_info_label">
                      <FaClock className="my_meetings_info_icon" />
                      Time
                    </div>
                    <div className="my_meetings_info_value">
                      {formatModalTime(selectedEvent.meet_time)}
                    </div>
                  </div>
                  
                  <div className="my_meetings_info_item my_meetings_full_width">
                    <div className="my_meetings_info_label">
                      <FaInfoCircle className="my_meetings_info_icon" />
                      Description
                    </div>
                    <div className="my_meetings_info_value my_meetings_event_description">
                      {selectedEvent.description || "No description provided."}
                    </div>
                  </div>
                </div>
                
                <div className="my_meetings_event_actions">
                  {selectedEvent.host?.url && (
                    <a 
                      href={selectedEvent.host.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`my_meetings_join_button ${selectedEvent.className.includes('joinable') ? 'active' : ''} ${selectedEvent.className.includes('upcoming-soon') ? 'upcoming' : ''}`}
                      onClick={(e) => !selectedEvent.className.includes('joinable') && e.preventDefault()}
                    >
                      <FaExternalLinkAlt className="my_meetings_action_icon" />
                      Join Meeting
                    </a>
                  )}
                  <button 
                    className="my_meetings_close_button"
                    onClick={handleCloseModal}
                  >
                    <FaTimes className="my_meetings_action_icon" />
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    </ErrorBoundary>
  );
}

export default MyMeetings;

// # Custom event rendering function
const renderEventContent = (eventInfo) => {
  const event = eventInfo.event;
  const statusClass = event.classNames.join(' ');
  const isPastButJoinable = eventInfo.event.start < new Date() && statusClass.includes('joinable');
  
  // # Use icon classes instead of React components
  let iconClass = '';
  
  if (statusClass.includes('joinable') && isPastButJoinable) {
    iconClass = 'fa-hourglass-half';
  } else if (statusClass.includes('joinable')) {
    iconClass = 'fa-arrow-right';
  } else if (statusClass.includes('upcoming-soon')) {
    iconClass = 'fa-clock';
  } else if (statusClass.includes('past-meeting')) {
    iconClass = 'fa-history';
  } else {
    iconClass = 'fa-calendar-alt';
  }
  
  return {
    html: `
      <div class="my_meetings_calendar_event ${statusClass}">
        <div class="my_meetings_calendar_event_title text-ellipsis" title="${event.title}">
          <i class="fa ${iconClass} my_meetings_calendar_event_status_icon"></i>
          ${event.title}
        </div>
        <div class="my_meetings_calendar_event_host text-ellipsis">
          ${event.extendedProps.hostData?.displayName || 'Loading...'}
        </div>
        <div class="my_meetings_calendar_event_time text-ellipsis">
          ${event.start.toLocaleTimeString([], { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          })}
          ${isPastButJoinable ? '<span class="my_meetings_event_active_tag">Active</span>' : ''}
        </div>
      </div>
    `
  };
};
